import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Descriptions, Divider } from 'antd'
import { PlusOutlined } from '@ant-design/icons'

import { Form, FormField } from '@/Core/UI/Form'
import { FormRow, FormSection } from '@/Ship/Components/Form/FormSection'
import { userUuidOf } from '@/Containers/ShiftSection/ShiftContainer/Helpers/driverVehicle'
import { clusterCodeOf, regionFilterFor } from '../Helpers/clusterRegion'
import { shiftFilterFor } from '../Helpers/shiftField'
import { getUnitLabel } from '../Helpers/unitLabel'
import { CreateTypicalRouteModal } from '../Components/Modals/CreateTypicalRouteModal'

/**
 * Fields the create/edit form is allowed to write. Everything else loaded into
 * `formContext.values` on edit (status, executor, delays, driverRatings,
 * _permissions, uuid, …) must NOT be sent back — a partial PATCH carrying nested
 * read-only graphs would fail denormalization. dispatcherComment is intentionally
 * excluded: the field is hidden here, and its old value must not be re-sent.
 */
const WRITABLE_FIELDS = [
    'driver',
    'vehicle',
    // Зміна водія — власне поле задачі (і для ERP-задач: в обмін не входить)
    'shift',
    'carrier',
    'isHired',
    'cluster',
    'region',
    // Sent at the task level; the backend stores it on the task's executor block.
    'dispatcher',
    'loadingPoint',
    'unloadingPoint',
    // Обраний диспетчером типовий маршрут. Бекенд денормалізує його routeUrl у картку.
    'typicalRoute',
    'plannedLoadingDate',
    'totalWeightTonnes',
    'tripsCount',
    // Контекст замовлення для standalone-задач (без заявки).
    'cargo',
    'constructionObject',
    'productionObject',
    'responsibleCustomer',
]

/**
 * Editing an ERP task (executor.vehicleNeed IS NOT NULL): only the task's own
 * fields are writable. The order context (loading/unloading points, objects,
 * responsible customer, dispatcher) lives on the linked transport request — those
 * are shown read-only and must not be re-sent as task-level overrides.
 */
const WRITABLE_FIELDS_WITH_REQUEST = [
    'driver',
    'vehicle',
    // Зміна водія — власне поле задачі (і для ERP-задач: в обмін не входить)
    'shift',
    'carrier',
    'isHired',
    // Кластер і регіон — власні поля задачі, редагуються й для ERP-задач
    'cluster',
    'region',
    'typicalRoute',
    'plannedLoadingDate',
    'totalWeightTonnes',
    'tripsCount',
]

/** Resolve an IRI string from a select value that may be an IRI or a resource object. */
const iriOf = (value: any): string | null =>
    !value ? null : typeof value === 'string' ? value : (value['@id'] ?? null)

/**
 * Resolve the numeric id from a select value that may be a resource object or an IRI string.
 * Needed for filters on relation columns (e.g. typicalRoute.loadingPoint) — the backend's
 * generic filter comparison binds the raw value straight into SQL with no IRI-to-id
 * resolution, so a loading_point_id integer column must be filtered by id, not by IRI.
 */
const idOf = (value: any): number | null => {
    if (!value) return null
    if (typeof value === 'string') return null
    return value.id ?? null
}

/** Carrier value in the form may be an IRI string or a resource object `{ @id, code }`. */
const carrierIriOf = (carrier: any): string | null =>
    !carrier ? null : typeof carrier === 'string' ? carrier : (carrier['@id'] ?? null)

const carrierCodeOf = (carrier: any): string | null => {
    if (!carrier) return null
    if (typeof carrier === 'object' && carrier['code']) return carrier['code']
    const iri = carrierIriOf(carrier)
    return iri ? (iri.split('/').pop() ?? null) : null
}

/**
 * Create/edit form for a standalone task (an assignment without a transport request).
 * Shared by both `create` and `edit` modes (AssignmentResource.detailConfig.modeConfig).
 *
 * Flow: pick "Найманий ТЗ" (isHired). When hired, a required "Перевізник" (carrier)
 * appears; the driver and vehicle selects are then filtered by the chosen carrier
 * (both entities carry a carrier on the backend) and stay disabled until a carrier
 * is picked. "Коментар диспетчера" is not shown here.
 *
 * The cascade is driven reactively via `mergeInputProps` on the existing fields
 * (driver/vehicle live in the shared formContext, so they stay serializable for
 * submit) — no field recreation, no isolated sub-form contexts.
 */
export const AssignmentCreateTemplate = observer(({ formContext, crudContext }: any) => {
    // ERP task (edit only): executor.vehicleNeed IS NOT NULL. Its order context
    // (points, cargo, objects, responsible customer) lives on the linked transport
    // request — shown read-only below, not as editable form fields. Standalone
    // tasks (create + edit) carry that context on the task itself and stay editable.
    const executor = formContext.getValue('executor')
    const vehicleNeed = executor?.vehicleNeed
    const hasRequest = !!vehicleNeed
    const transportRequest = vehicleNeed?.transportRequest

    const isHired = !!formContext.getValue('isHired')
    const carrier = formContext.getValue('carrier')
    const carrierIri = carrierIriOf(carrier)
    const carrierCode = carrierCodeOf(carrier)

    // Tracks the carrier we last reacted to, so we only clear driver/vehicle on a
    // REAL carrier change — not on the first mount in edit (which would wipe the
    // values just loaded from the backend). Initialised to the current carrier.
    const prevCarrierIri = useRef<string | null>(carrierIri)
    const prevIsHired = useRef<boolean>(isHired)
    const initialised = useRef(false)

    // Reactively re-filter / lock driver & vehicle when isHired or carrier changes.
    // Dependencies are primitives only (carrierIri/carrierCode), never the object,
    // to avoid an update loop. SelectInput is an observer and re-reads filter/disabled
    // from the observable inputProps, reloading its collection on next open.
    useEffect(() => {
        const driver = formContext.getField('driver')
        const vehicle = formContext.getField('vehicle')
        const carrierField = formContext.getField('carrier')

        // Carrier is required only when the transport is hired.
        carrierField?.setIsRequired(isHired)

        // The responsible dispatcher is required only for standalone tasks — that's
        // the only case the field is shown (see `!hasRequest && <FormField ...>`).
        // For ERP tasks it's hidden (inherited from the request), so keeping it
        // required would fail save-validation on an invisible field.
        formContext.getField('dispatcher')?.setIsRequired(!hasRequest)

        const locked = isHired && !carrierIri

        driver?.mergeInputProps({
            disabled: locked,
            filter: {
                filterRoles: { op: 'like', value: 'ROLE_DRIVER' },
                status: 'active',
                ...(isHired && carrierCode ? { 'carrier.code': carrierCode } : {}),
            },
        })

        vehicle?.mergeInputProps({
            disabled: locked,
            filter: isHired && carrierIri ? { carrier: carrierIri } : {},
        })

        // On a real carrier change, the previously selected driver/vehicle may no
        // longer belong to the new carrier — clear them. Skip the very first run so
        // edit-mode values loaded from the backend are preserved.
        if (initialised.current && prevCarrierIri.current !== carrierIri) {
            formContext.setValue('driver', null)
            formContext.setValue('vehicle', null)
        }

        // Turning "hired" off drops the carrier — own-fleet tasks have no carrier.
        // Only on a real transition, never on first mount.
        if (initialised.current && prevIsHired.current && !isHired && carrierIri) {
            formContext.setValue('carrier', null)
        }

        prevCarrierIri.current = carrierIri
        prevIsHired.current = isHired
        initialised.current = true
    }, [formContext, isHired, carrierIri, carrierCode, hasRequest])

    // Каскад «Кластер → Регіон»: регіон обирається серед регіонів обраного кластера й
    // заблокований, поки кластер не обрано. Справжня зміна кластера очищає регіон;
    // перший запуск — ні, щоб не стерти значення, завантажене при редагуванні.
    const clusterCode = clusterCodeOf(formContext.getValue('cluster'))
    const prevClusterCode = useRef<string | null>(clusterCode)
    const clusterInitialised = useRef(false)

    useEffect(() => {
        formContext.getField('region')?.mergeInputProps({
            disabled: !clusterCode,
            filter: regionFilterFor(clusterCode),
        })

        if (clusterInitialised.current && prevClusterCode.current !== clusterCode) {
            formContext.setValue('region', null)
        }
        prevClusterCode.current = clusterCode
        clusterInitialised.current = true
    }, [formContext, clusterCode])

    // Каскад «Водій → Зміна»: незавершені зміни обраного водія; без водія поле
    // заблоковане. Справжня зміна водія очищає зміну (бекенд не прийме зміну іншого
    // водія); перший запуск — ні, щоб не стерти значення при редагуванні.
    const driverUuid = userUuidOf(formContext.getValue('driver'))
    const prevDriverUuid = useRef<string | null>(driverUuid)
    const driverInitialised = useRef(false)

    useEffect(() => {
        formContext.getField('shift')?.mergeInputProps({
            disabled: !driverUuid,
            filter: shiftFilterFor(driverUuid),
        })

        if (driverInitialised.current && prevDriverUuid.current !== driverUuid) {
            formContext.setValue('shift', null)
        }
        prevDriverUuid.current = driverUuid
        driverInitialised.current = true
    }, [formContext, driverUuid])

    // Reactively filter the typical-route select by the current loading/unloading
    // points, so the dropdown only offers routes for the exact pair on the card.
    // The pair is filtered by the points' numeric ids (loading_point_id/unloading_point_id
    // are integer FK columns — the backend's generic filter binds the value as-is with
    // no IRI resolution, so filtering by IRI would 500). When the pair changes, a
    // previously chosen route no longer matches — clear it (skip first mount so a
    // backend-matched value loaded on open is preserved).
    // ERP task: the pair lives on the request (executor.vehicleNeed), not on the
    // hidden form fields — resolve the typical-route filter from there so the
    // dropdown still offers routes for the task's actual loading/unloading pair.
    const loadingPoint = hasRequest ? vehicleNeed?.loadingPoint : formContext.getValue('loadingPoint')
    const unloadingPoint = hasRequest ? vehicleNeed?.unloadingPoint : formContext.getValue('unloadingPoint')
    const loadingPointId = idOf(loadingPoint)
    const unloadingPointId = idOf(unloadingPoint)
    const loadingPointIri = iriOf(loadingPoint)
    const unloadingPointIri = iriOf(unloadingPoint)
    const prevPointsKey = useRef<string>(`${loadingPointId}|${unloadingPointId}`)
    const pointsInitialised = useRef(false)

    // "+ Створити новий маршрут" option at the bottom of the typicalRoute dropdown
    // (see СТВОРИТИ ЕЛЕМЕНТ requirement) — opens CreateTypicalRouteModal pinned to
    // the pair above; on save the new route is auto-selected into the field.
    const [createRouteOpen, setCreateRouteOpen] = useState(false)

    useEffect(() => {
        const routeField = formContext.getField('typicalRoute')
        if (!routeField) return

        routeField.mergeInputProps({
            filter: {
                ...(loadingPointId ? { loadingPoint: loadingPointId } : {}),
                ...(unloadingPointId ? { unloadingPoint: unloadingPointId } : {}),
            },
            dropdownRender: (menu: ReactNode) => (
                <>
                    {menu}
                    <Divider style={{ margin: '4px 0' }} />
                    <Button
                        type="text"
                        icon={<PlusOutlined />}
                        block
                        disabled={!loadingPointIri || !unloadingPointIri}
                        style={{ textAlign: 'left' }}
                        onClick={() => setCreateRouteOpen(true)}
                    >
                        Створити новий маршрут
                    </Button>
                </>
            ),
        })

        const pointsKey = `${loadingPointId}|${unloadingPointId}`
        if (pointsInitialised.current && prevPointsKey.current !== pointsKey) {
            formContext.setValue('typicalRoute', null)
        }
        prevPointsKey.current = pointsKey
        pointsInitialised.current = true
    }, [formContext, loadingPointId, unloadingPointId, loadingPointIri, unloadingPointIri])

    // Strip non-writable fields from the create/edit payload. An ERP task keeps a
    // narrower set (WRITABLE_FIELDS_WITH_REQUEST): the request-context fields must
    // not be re-sent as task-level overrides that would diverge from the request.
    const writableFields = hasRequest ? WRITABLE_FIELDS_WITH_REQUEST : WRITABLE_FIELDS
    useEffect(() => {
        const eventManager = crudContext?.eventManager
        if (!eventManager) return undefined

        eventManager.on(
            'onBeforeSendSaveRequest',
            'stripNonWritableAssignmentFields',
            ({ values }: { values: Record<string, any> }) => {
                Object.keys(values).forEach((key) => {
                    if (!writableFields.includes(key)) delete values[key]
                })

                // The dispatcher is held as an object in the form (so SelectInput can
                // render its label), but the backend resolves it via getResourceFromIri
                // and needs a bare IRI string. Normalize it here on submit.
                if (values.dispatcher && typeof values.dispatcher === 'object') {
                    values.dispatcher = iriOf(values.dispatcher)
                }
            }
        )

        return () => eventManager.off('onBeforeSendSaveRequest', 'stripNonWritableAssignmentFields')
    }, [crudContext, writableFields])

    // On edit the dispatcher arrives nested under executor.dispatcher; the form field
    // `dispatcher` is separate. Surface it reactively once the loaded values appear —
    // reading values here (vs. an onResponseEditRequest subscription) avoids a timing
    // race, since the template only mounts after loading finishes. Map once: don't
    // fight the user after they change it. ERP tasks don't edit the dispatcher here
    // (it belongs to the request context), so skip the mapping for them.
    //
    // Map the full user OBJECT, not its IRI: SelectInput builds the displayed option
    // (and its label via responseLabel) only from an object value — a bare IRI string
    // renders once then blanks out. The object is converted back to an IRI on submit
    // (see the dispatcher→IRI normalization in onBeforeSendSaveRequest below).
    const mappedDispatcher = useRef(false)
    const executorDispatcher = executor?.dispatcher
    useEffect(() => {
        if (hasRequest || mappedDispatcher.current) return
        if (executorDispatcher && !formContext.getValue('dispatcher')) {
            formContext.setValue('dispatcher', executorDispatcher)
            mappedDispatcher.current = true
        }
    }, [formContext, executorDispatcher, hasRequest])

    return (
        <Form formContext={formContext}>
            <div>
                {/* ERP task: order context comes from the linked request — read-only. */}
                {hasRequest && (
                    <FormSection>
                        <Descriptions title="Дані заявки" bordered size="small" column={1}>
                            <Descriptions.Item label="Номер заявки в ERP">
                                {transportRequest?.externalCode || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Вантаж">{vehicleNeed?.cargo?.title || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Пункт навантаження">
                                {vehicleNeed?.loadingPoint?.title || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Пункт розвантаження">
                                {vehicleNeed?.unloadingPoint?.title || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label={`Загальна маса перевезення, ${getUnitLabel(vehicleNeed?.unit)}`}>
                                {formContext.getValue('totalWeightTonnes') ?? '—'}
                            </Descriptions.Item>
                        </Descriptions>
                    </FormSection>
                )}

                <FormSection>
                    <FormField name="cluster" />
                    <FormField name="region" />
                </FormSection>

                <FormSection>
                    {/* Standalone tasks carry the responsible dispatcher on the task;
                        ERP tasks inherit it from the request. */}
                    {!hasRequest && <FormField name="dispatcher" />}
                    <FormField name="isHired" />
                    {isHired && <FormField name="carrier" />}
                    <FormField name="driver" />
                    <FormField name="vehicle" />
                    <FormField name="shift" />
                </FormSection>

                <FormSection>
                    {/* Loading/unloading points belong to the request for ERP tasks
                        (shown read-only above); only standalone tasks edit them here. */}
                    {!hasRequest && <FormField name="loadingPoint" />}
                    {!hasRequest && <FormField name="unloadingPoint" />}
                    <FormField name="typicalRoute" />
                    <FormField name="plannedLoadingDate" />
                </FormSection>

                <FormSection>
                    {/* Вантаж заявки показано read-only у картці «Дані заявки»;
                        standalone-задача несе власний і редагує його тут. */}
                    {!hasRequest && <FormField name="cargo" />}
                    {/* Короткі числа — в один ряд, щоб не тягнулись на всю ширину */}
                    <FormRow>
                        <FormField name="totalWeightTonnes" />
                        <FormField name="tripsCount" />
                    </FormRow>
                </FormSection>

                {/* Order context (objects, responsible customer) is request-owned for
                    ERP tasks — editable only for standalone tasks. */}
                {!hasRequest && (
                    <FormSection>
                        <FormField name="constructionObject" />
                        <FormField name="productionObject" />
                        <FormField name="responsibleCustomer" />
                    </FormSection>
                )}
            </div>

            <CreateTypicalRouteModal
                open={createRouteOpen}
                loadingPointIri={loadingPointIri}
                unloadingPointIri={unloadingPointIri}
                loadingPointTitle={loadingPoint?.title}
                unloadingPointTitle={unloadingPoint?.title}
                onClose={() => setCreateRouteOpen(false)}
                onCreated={(route) => formContext.setValue('typicalRoute', route)}
            />
        </Form>
    )
})
